import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { apiService } from "../service/Api";
import { Loader2 } from "lucide-react";
import CollateralPreview from "../components/collateral/CollateralPreview";
import CollateralChatbot from "../components/collateral/CollateralChatbot";

export default function Test() {
  const { key } = useParams();
  const [data, setData] = useState(null);
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Chatbot states
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);

  // Refs for stable callbacks
  const inputValueRef = useRef(inputValue);
  const isSendingLock = useRef(false);

  useEffect(() => {
    inputValueRef.current = inputValue;
  }, [inputValue]);

  useEffect(() => {
    if (!key) return;
    setLoading(true);
    setError(null);

    async function fetchData() {
      try {
        const response = await apiService.getCollateralwithTemplate(key);
        
        setData(response?.data?.data);
        setTemplate(response?.data?.template);
        
        console.log("Full data:", response?.data?.data);
        console.log("Template:", response?.data?.template);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch template or data.");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, [key]);

  const handleSendMessage = useCallback(async (overrideMessage) => {
    if (isSendingLock.current) return;

    const rawContent = typeof overrideMessage === 'string' ? overrideMessage : inputValueRef.current;
    const messageContent = rawContent.trim();

    if (!messageContent) return;

    const userMessage = {
      id: Date.now(),
      type: "user",
      content: messageContent,
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setIsSending(true);
    isSendingLock.current = true;

    try {
      const response = await apiService.editCollateral(key, messageContent);
      
      if (response?.data?.success) {
        // Update the data with new response
        const updatedData = response.data.data;
        const updatedTemplate = response.data.template;
        
        setData(updatedData);
        setTemplate(updatedTemplate);

        const botMessage = {
          id: Date.now() + 1,
          type: "bot",
          content: "✓ Collateral updated successfully! The preview has been refreshed with your changes.",
          timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          data: updatedData
        };

        setMessages(prev => [...prev, botMessage]);
      } else {
        throw new Error("Failed to update collateral");
      }
    } catch (err) {
      console.error("Error editing collateral:", err);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: "bot",
        content: "❌ Sorry, I couldn't process your request. Please try again.",
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        isError: true
      };

      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsSending(false);
      isSendingLock.current = false;
    }
  }, [key]);

  const handleInputChange = (e) => setInputValue(e.target.value);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleErrorReport = useCallback((errorMessage) => {
    // Set the error message in the input field
    setInputValue(errorMessage);
    // Focus the input field if possible (using a small timeout to ensure DOM is ready)
    setTimeout(() => {
      const textarea = document.querySelector('textarea[placeholder="Describe your changes..."]');
      if (textarea) {
        textarea.focus();
        // Scroll to the bottom of the textarea to show the content
        textarea.scrollTop = textarea.scrollHeight;
      }
    }, 100);
  }, []);

  const handleQuickEdit = useCallback(async (message) => {
    await handleSendMessage(message);
  }, [handleSendMessage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p className="text-gray-600">Loading collateral...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center text-red-600 bg-red-50 p-6 rounded-lg">
          <p className="font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  if (!template || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">No template or data received.</div>
      </div>
    );
  }


  return (
    <div className="flex h-screen bg-gray-50">
      {/* Left Side - Collateral Preview */}
      <CollateralPreview 
        template={template} 
        data={data} 
        withContainer={true}
        onErrorReport={handleErrorReport}
        onQuickEdit={handleQuickEdit}
      />

      {/* Right Side - Chatbot */}
      <CollateralChatbot
        messages={messages}
        inputValue={inputValue}
        isSending={isSending}
        onInputChange={handleInputChange}
        onSendMessage={handleSendMessage}
        onKeyPress={handleKeyPress}
      />
    </div>
  );
}